import React, { useEffect, useState } from "react";
import { Grid, CircularProgress, Card, CardContent, Toolbar } from '@mui/material';
import axios from 'axios';
import Config from '../../config';
import Helpers from '../../helpers';
import Moment from 'react-moment';
import NotFound from "../../components/not_found";

type ReportEntryProps = {
    label?: string;
    contentStyle?: React.CSSProperties;
    children?: React.ReactNode;
};

const ReportEntry = (props: ReportEntryProps) => {
    if(!props.children){
        return '';
    }
    return <div style={{marginBottom: '10px'}}>
        <span style={{width: '10em', fontWeight: 'bold', display: 'inline-block', verticalAlign: 'top', marginRight: '10px'}}>{props.label}</span>
        <span style={{whiteSpace: 'pre', display: 'inline-block', ...props.contentStyle}}>{props.children}</span>
    </div>
};

type Certificate = {
    Subject?: string;
    SerialNumber?: string;
    Issuer?: string;
    ExpirationDate?: string;
    PublicKey?: string;
};

type ReportCertificateProps = {
    label?: string;
    cert?: Certificate;
};

const ReportCertificate = (props: ReportCertificateProps) => {
    if(!props.cert){
        return '';
    }
    let content = `Subject: ${props.cert.Subject}\nSerial number: ${props.cert.SerialNumber}\nIssuer: ${props.cert.Issuer}\nExpiration: ${props.cert.ExpirationDate}\nPublic key:\n${props.cert.PublicKey}`;
    return <ReportEntry label={props.label}>{content}</ReportEntry>
};

type EdiTestReport = {
    URL?: string;
    Date?: string;
    AS2From?: string;
    AS2To?: string;
    MessageId?: string;
    FileName?: string;
    TestFile?: string;
    SignatureAlgorithm?: string;
    EncryptionAlgorithm?: string;
    SigningCertificate?: Certificate;
    RolloverCertificate?: Certificate;
    EncryptionCertificate?: Certificate;
    MDNOptions?: string;
    MDNReceipt?: { Content?: string };
    ErrorMessage?: string;
};

type EdiReportState = {
    loaded: boolean;
    reports?: EdiTestReport[];
    notFound?: boolean;
};

const EdiReport = () => {
    const [state, setState] = useState<EdiReportState>({loaded: false});

    useEffect(() => {
        (async () => {
            let reportId = new Helpers().queryString()['reportId'];
            try {
                let reports = (await axios.get(Config.api + '/odata/Company/Functions.GetEdiTestReport?reportId=' + reportId)).data.reports;
                setState(prev => ({...prev, reports, loaded: true}));
            }catch{
                setState(prev => ({...prev, notFound: true, loaded: true}));
            }
        })();
    }, []);

    if(!state.loaded){
        return <Grid container spacing={2}>
        <Grid size={{ md: 12 }}>
            <Card>
            <div style={{padding: '1em'}}>Downloading file</div>
            <div style={{position: 'relative'}}>
                <CircularProgress variant={'indeterminate'} size='8em'/>
            </div>
            </Card>
        </Grid>
    </Grid>;
    }
    if(state.notFound){
        return <NotFound/>;
    }
    return <Grid container spacing={2}>
        {state.reports.map((r, i) => <Grid key={i} size={12}>
                <Card>
                    <Toolbar className='lbtoolbar'>Attempt #{i + 1}</Toolbar>
                    <CardContent style={{display: 'flex', flexDirection: 'column', textAlign: 'left'}}>
                        <ReportEntry label='URL'>{r.URL}</ReportEntry>
                        <ReportEntry label='Date'>{!(!r.Date) && <Moment utc format='MM/DD/YYYY HH:mm:ss UTC'>{r.Date}</Moment>}</ReportEntry>
                        <ReportEntry label='AS2 Sender'>{r.AS2From}</ReportEntry>
                        <ReportEntry label='AS2 Receiver'>{r.AS2To}</ReportEntry>
                        <ReportEntry label='Message ID'>{r.MessageId}</ReportEntry>
                        <ReportEntry label='File Name'>{r.FileName}</ReportEntry>
                        <ReportEntry label='File Content'>{r.TestFile}</ReportEntry>
                        <ReportEntry label='Signature Algorithm'>{r.SignatureAlgorithm}</ReportEntry>
                        <ReportEntry label='Encryption Algorithm'>{r.EncryptionAlgorithm}</ReportEntry>
                        <ReportCertificate label='Signing Certificate' cert={r.SigningCertificate}/>
                        <ReportCertificate label='Secondary Signing Certificate' cert={r.RolloverCertificate}/>
                        <ReportCertificate label='Encryption Certificate' cert={r.EncryptionCertificate}/>
                        <ReportEntry label='MDN Options'>{r.MDNOptions}</ReportEntry>
                        <ReportEntry label='MDN'>{(r.MDNReceipt || {}).Content}</ReportEntry>
                        <ReportEntry label='Error' contentStyle={{color: 'red'}}>{r.ErrorMessage}</ReportEntry>
                    </CardContent>
                </Card>
            </Grid>)}
    </Grid>
};

export default EdiReport;
